/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ApolloError, gql, useMutation } from "@apollo/client";

import { useErrorsOrSuccessHandler } from "graphql/errorHandlers";
import {
  ERROR_FRAGMENT,
  ErrorFragment,
  FREELANCE_PROFILE_FRAGMENT,
  FreelanceProfileFragment,
} from "graphql/fragments";
import {
  FREELANCE_PROFILES,
  FreelanceProfilesQuery,
  SESSION,
  SessionQuery,
} from "graphql/queries";
import { CreateFreelanceProfileAttributes } from "graphql/types";

const CREATE_FREELANCE_PROFILE = gql`
  mutation ($attributes: CreateFreelanceProfileAttributes!) {
    createFreelanceProfile(input: { attributes: $attributes }) {
      freelanceProfile {
        ...FreelanceProfileFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${FREELANCE_PROFILE_FRAGMENT}
  ${ERROR_FRAGMENT}
`;

interface Payload {
  freelanceProfile?: FreelanceProfileFragment | null;
  errors: ErrorFragment[];
}

interface CreateFreelanceProfileMutation {
  createFreelanceProfile: Payload | null;
}

export const useCreateFreelanceProfileMutation = () => {
  const handleErrors = useErrorsOrSuccessHandler();

  const [mutation, { data, loading }] = useMutation<
    CreateFreelanceProfileMutation,
    { attributes: CreateFreelanceProfileAttributes }
  >(CREATE_FREELANCE_PROFILE);

  const createFreelanceProfile = (
    { attributes }: { attributes: CreateFreelanceProfileAttributes },
    onSuccess?: (freelanceProfile: FreelanceProfileFragment) => void
  ) =>
    mutation({
      variables: { attributes },
      update(cache, { data: newProfileData }) {
        const newProfileNode =
          newProfileData?.createFreelanceProfile?.freelanceProfile;
        if (!newProfileNode) return;

        cache.modify({
          fields: {
            session() {
              const sessionQuery = cache.readQuery<SessionQuery>({
                query: SESSION,
              });
              if (!sessionQuery) return;

              cache.writeQuery<SessionQuery>({
                query: SESSION,
                data: {
                  session: {
                    ...sessionQuery.session,
                    account: {
                      ...sessionQuery.session.account,
                      hasFreelanceProfiles: true,
                    },
                  },
                },
              });
            },
            freelanceProfiles() {
              const existingProfiles = cache.readQuery<FreelanceProfilesQuery>({
                query: FREELANCE_PROFILES,
              });
              if (!existingProfiles) return;

              const items = [
                ...(existingProfiles.freelanceProfiles.items ?? []),
                newProfileNode,
              ];

              cache.writeQuery<FreelanceProfilesQuery>({
                query: FREELANCE_PROFILES,
                data: {
                  freelanceProfiles: {
                    items,
                    total: existingProfiles.freelanceProfiles.total + 1,
                  },
                },
              });
            },
          },
        });
      },
    })
      .then(({ data, errors }) => {
        const dataErrors = data?.createFreelanceProfile?.errors ?? [];
        const profile = data?.createFreelanceProfile?.freelanceProfile;

        handleErrors({
          dataErrors,
          graphqlErrors: errors,
          onSuccess: () => onSuccess && profile && onSuccess(profile),
          onRetry: () => createFreelanceProfile({ attributes }, onSuccess),
        });
      })
      .catch((_error: ApolloError) => null);

  return {
    createFreelanceProfile,
    isLoading: loading,
    freelanceProfile: data?.createFreelanceProfile?.freelanceProfile,
    errors: data?.createFreelanceProfile?.errors ?? [],
  };
};
